const cookieProducts = 'rechargebundle_products';

if (!customElements.get('rb-amount')) {
  class RechargeAmount extends HTMLElement {
    connectedCallback() {
      this._boundReady = this.onRechargeReady.bind(this);
      this._boundVariant = this.onVariantChange.bind(this);
      this._boundFilter = this.onFilterChange.bind(this);
      this._boundButtonClick = this.onButtonClick.bind(this);
      this._boundProductClick = this.onProductClick.bind(this);
      this._buttonListenerAttached = false;

      document.addEventListener("bundling-widget:ready", this._boundReady);
      document.addEventListener("recharge:variant", this._boundVariant);
      document.addEventListener("recharge:filter", this._boundFilter);

      this.recharge = {
        buttons: null,
        inputs: null
      }

      //Quantities per rb-item id — a filter unmounts the inputs of items
      //that stay booked, so the DOM alone is not enough.
      this.selectedQtys = new Map();
    }

    disconnectedCallback() {
      document.removeEventListener("bundling-widget:ready", this._boundReady);
      document.removeEventListener("recharge:variant", this._boundVariant);
      document.removeEventListener("recharge:filter", this._boundFilter);
    }

    onRechargeReady() {
      if (!this._buttonListenerAttached) {
        const button = this.querySelector('button');
        button.addEventListener('click', this._boundButtonClick);
        this._buttonListenerAttached = true;
      }

      setTimeout(() => {
        if (this.recharge.inputs) {
          this.recharge.inputs.forEach(input => input.removeEventListener('change', this._boundProductClick));
        }
        if (this.recharge.buttons) {
          this.recharge.buttons.forEach(button => button.removeEventListener('click', this._boundProductClick));
        }

        this.recharge.inputs = Array.from(document.querySelectorAll('.rb-item-actions input'));
        this.recharge.inputs.forEach(input => input.addEventListener('change', this._boundProductClick));

        this.recharge.buttons = Array.from(document.querySelectorAll('.rb-item-actions button'));
        this.recharge.buttons.forEach(button => button.addEventListener('click', this._boundProductClick));

        this.syncSelectedQtys();
      }, 200)
    }

    syncSelectedQtys() {
      //Only rendered items are reconciled — filtered-out ones keep their entry.
      document.querySelectorAll('.rb-item').forEach(item => {
        const id = item.dataset.rbItemId;
        const input = item.querySelector('.rb-input');
        if(!id || !input) {
          return;
        }

        const quantity = parseInt(input.value, 10) || 0;
        if(quantity > 0) {
          this.selectedQtys.set(id, quantity);
        } else {
          this.selectedQtys.delete(id);
        }
      });
    }

    onVariantChange(event) {
      const element = this.querySelector('[data-title]');
      const progress = this.querySelector('progress');

      const { title } = event.detail;
      if(!title || !element) {
        return console.error('RechargeAmount', 'no title found')
      }

      //Update Element
      element.innerText = title;

      //Update Progress
      progress.max = title.replace(/\D/g, "");

      //Update Bars
      this.updateBars();
      
      //Re-render again
      this.onRechargeReady();
    }

    onFilterChange(event) {
      //Re-render again
      this.onRechargeReady();
    }

    onProductClick() {
      //Recharge patches the input value and [data-rb-added] asynchronously,
      //so reading them on the click itself is always one meal behind.
      setTimeout(() => {
        this.updateBars();
        this.dispatchEvent();
      }, 0);
    }

    onButtonClick() {
      const target = document.querySelector('.rb-price-btn-wrapper .rb-btn');
      target.click();

      //Remove input values
      this.clearProducts();
    }

    updateBars() {
      const label = this.querySelector('[data-value]')
      const progress = this.querySelector('progress');
      const button = this.querySelector('button');

      if(!this.recharge.inputs || !label || !progress) {
        return console.error('RechargeAmount', 'no inputs')
      }

      //Calculate everything together
      const input = document.querySelector('[data-rb-added]');
      const value = parseInt(input.dataset.rbAdded);
      label.innerText = value;

      //Update Progress Bar
      progress.value = value;

      //Activate Button if value is max
      if(progress.value === progress.max) {
        button.removeAttribute('hidden');
      } else {
        button.hidden = true;
      }
    }

    clearProducts() {
      //Remove products
      const products = this.recharge.inputs.filter(input => input.value > 0);
      products.forEach(input => {
        input.value = 0;
        input.dispatchEvent(new Event('change'));
      });

      //Filtered-out items only live in the map, so clear it too.
      this.selectedQtys.clear();

      //Remove Cookies
      Shopify.LocalStorage.remove(cookieProducts);

      //Reset Progress — do this directly rather than via updateBars(), which reads
      //[data-rb-added] from the ReCharge Vue DOM. Vue batches DOM updates
      //asynchronously so [data-rb-added] is still stale at this point and would
      //override the reset with the old value.
      const progress = this.querySelector('progress');
      const label = this.querySelector('[data-value]');
      const button = this.querySelector('button');
      progress.value = 0;
      if (label) label.innerText = 0;
      if (button) button.hidden = true;

      //Dispatch again
      this.dispatchEvent();

      //Close Modal
      document.dispatchEvent(new CustomEvent('recharge:modal:close'))
    }

    currencyToNumber(price) {
      //Handles both en-US ("1,234.56") and fr-FR ("1 234,56") number formats.
      //Whichever of "." or "," appears last is treated as the decimal separator.
      const cleaned = price.replace(/[^0-9,.-]+/g, '');
      const lastComma = cleaned.lastIndexOf(',');
      const lastDot = cleaned.lastIndexOf('.');
      if(lastComma > lastDot) {
        return Number(cleaned.replace(/\./g, '').replace(',', '.'));
      }
      return Number(cleaned.replace(/,/g, ''));
    }

    tierPercentage(quantity) {
      //`data-tier-discounts` mirrors the Recharge bundle's tiered discounts
      //as "<minimum quantity>:<percent>"; the highest matching tier wins.
      const tiers = (this.dataset.tierDiscounts || '').split(',');

      return tiers.reduce((percentage, tier) => {
        const [minimum, value] = tier.split(':').map(Number);
        return quantity >= minimum ? Math.max(percentage, value || 0) : percentage;
      }, 0);
    }

    getTotals() {
      //Recharge ROUNDS the tier discount per meal in its rendered `.rb-price`,
      //the cart FLOORS it — up to one cent per meal apart. Sum the Liquid meal
      //prices and floor like the cart instead. Returns { price (subscription),
      //compare_at_price (one-time) } in CENTS, or null without meal data.
      this.syncSelectedQtys();
      if(this.selectedQtys.size === 0) {
        return null;
      }

      let quantity = 0;
      this.selectedQtys.forEach(qty => quantity += qty);
      const percentage = this.tierPercentage(quantity);

      let price = 0;
      let compare_at_price = 0;
      let found = false;

      this.selectedQtys.forEach((qty, id) => {
        const meal = document.querySelector(`[data-product="${id}"]`);
        const base = meal ? parseInt(meal.dataset.mealPrice, 10) || 0 : 0;
        if(base <= 0) {
          return;
        }

        const subscription = parseInt(meal.dataset.subPrice, 10) || base;
        price += (subscription - Math.floor(subscription * percentage / 100)) * qty;
        compare_at_price += (base - Math.floor(base * percentage / 100)) * qty;
        found = true;
      });

      return found ? { price, compare_at_price } : null;
    }

    getPrices() {
      //Get prices
      const element = document.querySelector('.rb-price > span[aria-label]');
      const label = element.getAttribute('aria-label')

      let prices = {
        price: 0,
        compare_at_price: 0
      }

      //Check if label contains specific wording
      if(label.includes('Original price')) {
        const results = label.split('. ').map(result => this.currencyToNumber(result));

        prices.compare_at_price = Math.round(results[0] * 100);
        prices.price = Math.round(results[1] * 100);
      } else {
        prices.price = Math.round(this.currencyToNumber(element.innerText) * 100)
      }

      return prices;
    }

    dispatchEvent() {
      const progress = this.querySelector('progress');

      //Products
      document.dispatchEvent(new CustomEvent('recharge:progress', {
        detail: {
          value: progress.value,
          max: progress.max,
          full: progress.value === progress.max,
          prices: this.getPrices(),
          totals: this.getTotals()
        }
      }))
    }
  }
  customElements.define('rb-amount', RechargeAmount);
}
